/**
 * Creation-source tracking — records HOW each new site was created so the admin
 * dashboard can show a per-brand breakdown plus a combined overall total.
 *
 * Forward-only ("tracked since 2026-06-24"): there is no historical backfill, so
 * the counters reflect sites created from launch onward. Existing sites are not
 * inferred, because the two AI buckets (ai_convert / plugin exports) are not
 * recoverable from old docs and a backfill would count approximations.
 *
 * Mirrors the aeroGate funnel counter EXACTLY (FieldValue.increment, merge,
 * errors swallowed) and writes to a SEPARATE doc (stats/creationSource), so it
 * can never touch the A/B (stats/abtest) or Aero gate (stats/aeroGateFunnel)
 * counters. It is always fire-and-forget: the create/deploy write must never
 * depend on, wait for, or fail because of it.
 */
import firebase from '../firebase';

// The creation-source enum. plugin_export is intentionally MERGED into
// html_drop: a plugin-exported HTML file is dropped into the same dropzone and
// is indistinguishable from a manual HTML upload at write time, so html_drop
// covers both. The four buckets:
//   html_drop    HTML upload — manual drop OR plugin-exported HTML (merged)
//   figma_embed  embed a Figma prototype
//   ai_convert   a Figma design converted with AI (convert-then-drop in the UI,
//                and convert-then-agent recovered server-side from draftMeta)
//   ai_agent     Claude/ChatGPT agent builds and publishes via the REST deploy
export const CREATION_SOURCES = ['html_drop', 'figma_embed', 'ai_convert', 'ai_agent'];

// Human labels for the admin dashboard, in display order.
export const CREATION_SOURCE_LABELS = [
  ['html_drop', 'HTML upload'],
  ['figma_embed', 'Figma embed'],
  ['ai_convert', 'AI convert (Figma)'],
  ['ai_agent', 'AI agent'],
];

// Fire-and-forget per-brand creation-source counter into stats/creationSource.
// Writes a per-brand key plus a brand-agnostic total, exactly like the aeroGate
// funnel writes a per-source key plus a per-event total. brand: 'aero' |
// 'figmafolio'. source: one of CREATION_SOURCES. Never throws into the caller.
export function recordCreationSource(source, brand) {
  if (!source || !brand) return;
  try {
    const db = firebase.firestore();
    const inc = firebase.firestore.FieldValue.increment(1);
    db.collection('stats')
      .doc('creationSource')
      .set(
        { [`source_${brand}_${source}`]: inc, [`source_total_${source}`]: inc },
        { merge: true }
      )
      .catch((err) => {
        console.warn('[creationSource] counter increment failed (non-blocking):', err && err.message);
      });
  } catch (err) {
    // Stats are non-critical; never surface to the create/deploy flow.
    console.warn('[creationSource] counter increment failed (non-blocking):', err && err.message);
  }
}
